/* ─────────────────────────────────────────────────────────────────
 *  SignFieldNodeView — 보호자 서명 모드 전용 인터랙티브 inline 필드
 *
 *  편집기(ContractRichEditor)를 그대로 재사용해 계약 본문을 렌더하되,
 *  서명 모드에서는 inline contractField 를 "정적 박스" 대신 이 NodeView 로
 *  렌더해 서명패드/체크박스/입력을 실제 동작하게 한다.
 *
 *  ⚠ TipTap React NodeView 는 외부 스토어 구독만으론 리렌더가 확실치 않다.
 *  → 각 NodeView 가 자체 useState 로 값을 소유(리렌더 확실)하고,
 *    수집용으로만 SignStore 에 write + notify(진행률 push).
 * ───────────────────────────────────────────────────────────────── */
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface SignStore {
  values: Record<string, string>;
  checkboxes: Record<string, boolean>;
  signature: string | null;
  sealImageUrl: string | null;
  readOnly: boolean;
  setValue: (key: string, value: string) => void;
  toggleCheckbox: (key: string, checked: boolean) => void;
  setSignature: (dataUrl: string) => void;
  /** 값 변경 통지(진행률 push 용) — 자기 자신을 콜백에 넘겨 동일 스토어를 읽게 한다. */
  notify: () => void;
  _onChange: (store: SignStore) => void;
}

export function createSignStore(
  sealImageUrl: string | null,
  readOnly: boolean,
  onChange: (store: SignStore) => void,
): SignStore {
  return {
    values: {},
    checkboxes: {},
    signature: null,
    sealImageUrl: sealImageUrl ?? null,
    readOnly,
    _onChange: onChange,
    notify() {
      this._onChange(this);
    },
    setValue(key, value) {
      this.values[key] = value;
      this.notify();
    },
    toggleCheckbox(key, checked) {
      this.checkboxes[key] = checked;
      this.notify();
    },
    setSignature(dataUrl) {
      this.signature = dataUrl;
      this.notify();
    },
  };
}

/* ─────────────────────────────────────────────────────────────────
 *  SignaturePadModal — 서명 캡처(캔버스 → dataURL)
 * ───────────────────────────────────────────────────────────────── */
export const SignaturePadModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onApply: (dataUrl: string) => void;
}> = ({ open, onClose, onApply }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    if (!open) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
    dirty.current = false;
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const pos = (e: React.PointerEvent) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * c.width,
      y: ((e.clientY - r.top) / r.height) * c.height,
    };
  };
  const start = (e: React.PointerEvent) => {
    const c = canvasRef.current!;
    const ctx = c.getContext("2d")!;
    drawing.current = true;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    c.setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    dirty.current = true;
  };
  const end = () => {
    drawing.current = false;
  };
  const clear = () => {
    const c = canvasRef.current!;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    dirty.current = false;
  };
  const apply = () => {
    if (!dirty.current) {
      onClose();
      return;
    }
    onApply(canvasRef.current!.toDataURL("image/png"));
  };

  // PanZoom 의 transform 컨테이너 안에서 position:fixed 가 뷰포트 기준이 안 되므로
  // document.body 로 portal → 스크롤/스케일과 무관하게 화면에 고정.
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 440,
          background: "#fff",
          borderRadius: 16,
          padding: 16,
        }}
      >
        <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 700, color: "#333" }}>
          보호자 서명
        </div>
        <canvas
          ref={canvasRef}
          width={600}
          height={240}
          style={{
            width: "100%",
            height: 200,
            border: "1px dashed #CCC",
            borderRadius: 8,
            touchAction: "none",
          }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={clear}
            style={{
              flex: 1,
              padding: "10px 0",
              borderRadius: 12,
              border: "1px solid #E9E9E9",
              color: "#888",
              fontWeight: 600,
            }}
          >
            지우기
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: "10px 0",
              borderRadius: 12,
              border: "1px solid #E9E9E9",
              color: "#888",
              fontWeight: 600,
            }}
          >
            취소
          </button>
          <button
            type="button"
            onClick={apply}
            style={{
              flex: 2,
              padding: "10px 0",
              borderRadius: 12,
              background: "#FF9934",
              color: "#fff",
              fontWeight: 700,
            }}
          >
            서명 사용
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const fieldColor = (type: string): string => {
  if (type === "signature") return "#F472B6";
  if (type === "seal") return "#F59E0B";
  if (type === "checkbox") return "#22A55B";
  if (type === "date") return "#3B82F6";
  return "#8B5CF6";
};
const fieldIcon = (type: string): string => {
  if (type === "signature") return "✏️";
  if (type === "seal") return "🔖";
  if (type === "checkbox") return "☑️";
  return "✍️";
};

export const SignFieldNodeView: React.FC<NodeViewProps> = ({ node, editor }) => {
  const a = (node.attrs || {}) as Record<string, any>;
  const type: string = a.fieldType || "text";
  const fieldKey: string = a.fieldKey || "";
  const label: string = a.label || fieldKey || "필드";
  const color = fieldColor(type);
  const store: SignStore | undefined = (editor.storage as any)?.sign;
  const readOnly = store?.readOnly ?? true;

  // NodeView 가 값을 자체 소유 → 리렌더 확실. store 는 수집용 write.
  const [signature, setSignature] = useState<string | null>(store?.signature ?? null);
  const [checked, setChecked] = useState<boolean>(!!store?.checkboxes[fieldKey]);
  const [text, setText] = useState<string>(store?.values[fieldKey] ?? "");
  const [padOpen, setPadOpen] = useState(false);

  if (!store) {
    return (
      <NodeViewWrapper as="span" className="contract-field-inline">
        <span style={{ color, fontSize: 12 }}>
          {fieldIcon(type)} {label}
        </span>
      </NodeViewWrapper>
    );
  }

  // 도장 (사업자 값 — 이미지 표시만)
  if (type === "seal") {
    return (
      <NodeViewWrapper
        as="span"
        data-field-key={fieldKey}
        data-field-type="seal"
        className="contract-field-inline"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          verticalAlign: "middle",
          width: 64,
          height: 64,
          margin: "0 2px",
        }}
      >
        {store.sealImageUrl ? (
          <img
            src={store.sealImageUrl}
            alt={label}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <span
            style={{
              width: "72%",
              height: "72%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `2px dashed ${color}`,
              borderRadius: "50%",
              color,
              fontSize: 11,
            }}
          >
            {fieldIcon(type)}
          </span>
        )}
      </NodeViewWrapper>
    );
  }

  // 서명
  if (type === "signature") {
    return (
      <NodeViewWrapper
        as="span"
        data-field-key={fieldKey}
        data-field-type="signature"
        className="contract-field-inline"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          verticalAlign: "middle",
          minWidth: 150,
          height: 40,
          margin: "0 2px",
          // 서명이 채워지면 점선 테두리·배경 제거 → 서명 이미지만 남는다.
          border: signature ? "none" : `1.5px dashed ${color}`,
          borderRadius: 4,
          background: signature ? "transparent" : "#fff",
          overflow: "hidden",
        }}
        contentEditable={false}
      >
        <button
          type="button"
          disabled={readOnly}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => !readOnly && setPadOpen(true)}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            color,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {signature ? (
            <img
              src={signature}
              alt={label}
              style={{ height: "100%", width: "100%", objectFit: "contain" }}
            />
          ) : (
            <span>
              {fieldIcon(type)} {label}
            </span>
          )}
        </button>
        <SignaturePadModal
          open={padOpen}
          onClose={() => setPadOpen(false)}
          onApply={(dataUrl) => {
            setSignature(dataUrl);
            store.setSignature(dataUrl);
            setPadOpen(false);
          }}
        />
      </NodeViewWrapper>
    );
  }

  // 체크박스
  if (type === "checkbox") {
    return (
      <NodeViewWrapper
        as="span"
        data-field-key={fieldKey}
        data-field-type="checkbox"
        className="contract-field-inline"
        style={{
          display: "inline-flex",
          verticalAlign: "middle",
          width: 18,
          height: 18,
          margin: "0 2px",
        }}
        contentEditable={false}
      >
        <button
          type="button"
          disabled={readOnly}
          aria-label={label}
          title={label}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (readOnly) return;
            const next = !checked;
            setChecked(next);
            store.toggleCheckbox(fieldKey, next);
          }}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px solid ${color}`,
            borderRadius: 3,
            background: "#fff",
            color: "#FF9934",
            fontSize: 13,
            lineHeight: 1,
          }}
        >
          {checked ? "✓" : ""}
        </button>
      </NodeViewWrapper>
    );
  }

  // 텍스트 / 날짜
  if (readOnly) {
    return (
      <NodeViewWrapper
        as="span"
        data-field-key={fieldKey}
        data-field-type={type}
        className="contract-field-inline"
      >
        <span style={{ color: "#111", fontSize: 12 }}>{text || label}</span>
      </NodeViewWrapper>
    );
  }
  return (
    <NodeViewWrapper
      as="span"
      data-field-key={fieldKey}
      data-field-type={type}
      className="contract-field-inline"
      style={{
        display: "inline-flex",
        verticalAlign: "middle",
        minWidth: 120,
        height: 24,
        margin: "0 2px",
      }}
      contentEditable={false}
    >
      <input
        type={type === "date" ? "date" : "text"}
        value={text}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          setText(e.target.value);
          store.setValue(fieldKey, e.target.value);
        }}
        placeholder={label}
        style={{
          width: "100%",
          height: "100%",
          borderBottom: `2px dashed ${color}`,
          background: "transparent",
          color: "#111",
          fontSize: 12,
          padding: "0 4px",
        }}
      />
    </NodeViewWrapper>
  );
};
